import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Trash2 } from "lucide-react";
import { api, fmtDate, errMsg } from "@/lib/api";

const TIPO_LBL = { eficacia: "Rev. eficacia", procesamiento: "Rev. procesamiento" };

const RevRow = ({ r, onChange }) => {
  const upd = async (k, v) => {
    try {
      await api.put(`/revisiones-terapia/${r.id}`, { ...r, [k]: v });
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm("¿Borrar esta revisión?")) return;
    await api.delete(`/revisiones-terapia/${r.id}`);
    onChange();
  };
  return (
    <div className="py-2 text-xs" data-testid={`rev-terapia-${r.id}`}>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-800">{TIPO_LBL[r.tipo]}</span>
        <input type="date" value={r.fecha || ""} onChange={(e) => upd("fecha", e.target.value)} data-testid={`rev-terapia-fecha-${r.id}`} className="rounded border border-slate-200 px-1 py-0.5" />
        <span className="text-slate-500">sesión {r.sesion_n}</span>
        <button onClick={del} data-testid={`rev-terapia-del-${r.id}`} className="ml-auto text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="mt-1 flex gap-4 text-slate-600">
        <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.informe_hecho} onChange={(e) => upd("informe_hecho", e.target.checked)} data-testid={`rev-terapia-hecho-${r.id}`} />Informe hecho</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.informe_entregado} onChange={(e) => upd("informe_entregado", e.target.checked)} data-testid={`rev-terapia-entregado-${r.id}`} />Informe entregado</label>
      </div>
    </div>
  );
};

const Estado = ({ toca, children, testId }) => (
  <div data-testid={testId} className={`rounded-md px-3 py-2 text-xs ${toca ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-slate-50 text-slate-700 border border-slate-200"}`}>{children}</div>
);

export const ControlTerapiaPaciente = ({ patientId, reload }) => {
  const [c, setC] = useState(null);
  const load = useCallback(() => {
    api.get(`/patients/${patientId}/control-terapia`).then((r) => setC(r.data));
  }, [patientId]);
  useEffect(() => {
    load();
  }, [load, reload]);
  if (!c?.tiene_terapia) return null;
  const apuntar = async (tipo) => {
    try {
      await api.post(`/patients/${patientId}/revisiones-terapia`, { tipo });
      toast.success(`${TIPO_LBL[tipo]} apuntada. Marca el informe cuando esté hecho y entregado.`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="control-terapia-paciente">
      <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="h-4 w-4 text-slate-500" /><h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Revisiones de terapia visual</h3>
        <span className="ml-auto text-xs text-slate-500" data-testid="ct-sesiones">{c.sesiones} sesiones</span></div>
      <div className="space-y-2">
        <Estado toca={c.toca_eficacia} testId="ct-eficacia">
          <b>Eficacia</b> (cada 12 sesiones): {c.toca_eficacia ? `toca ya (sesión ${c.prox_eficacia_sesion})` : `en la sesión ${c.prox_eficacia_sesion}, faltan ${c.faltan_eficacia}`}
        </Estado>
        <Estado toca={c.toca_procesamiento} testId="ct-procesamiento">
          <b>Procesamiento</b> (9 meses desde la 1ª sesión): {c.procesamiento_hecho ? "hecha" : !c.fecha_procesamiento ? "aún no ha empezado la terapia" : c.toca_procesamiento ? `toca desde el ${fmtDate(c.fecha_procesamiento)}` : `el ${fmtDate(c.fecha_procesamiento)}`}
        </Estado>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => apuntar("eficacia")} data-testid="ct-apuntar-eficacia" className="rounded-md border border-slate-300 px-2.5 py-1 text-xs hover:bg-slate-50">+ Revisión de eficacia hecha</button>
        <button onClick={() => apuntar("procesamiento")} data-testid="ct-apuntar-procesamiento" className="rounded-md border border-slate-300 px-2.5 py-1 text-xs hover:bg-slate-50">+ Revisión de procesamiento hecha</button>
      </div>
      {c.revisiones.length > 0 && <div className="mt-2 divide-y divide-slate-100">{[...c.revisiones].reverse().map((r) => <RevRow key={r.id} r={r} onChange={load} />)}</div>}
    </div>
  );
};
